import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<PatientProjectionListStep />` — list of patient-projection records
 * decrypted in active session and rendered into patient-comprehensible
 * narrative via the client-side taxonomy.
 *
 * **Pattern B doctrinal posture: rendering happens here, not at SHT.**
 * Each record's encrypted content is a `PatientProjectionContent`
 * envelope (audit-chain or aggregated-payload); the parent view
 * decrypts and passes the cleartext envelopes here. For audit-chain
 * envelopes, this step calls `taxonomy.render(...)` to produce the
 * narrative the patient sees.
 */
import * as React from 'react';
import { useLocale } from '../../i18n/locale-context.js';
export function PatientProjectionListStep(props) {
    const { t } = useLocale();
    const [items, setItems] = React.useState([]);
    React.useEffect(() => {
        let cancelled = false;
        void (async () => {
            const next = [];
            for (const item of props.records) {
                const recordId = item.decrypted.record.recordId;
                const anchoredAt = item.decrypted.record.projectionAnchoredAt;
                if (item.content.kind === 'audit-chain') {
                    try {
                        const rendered = await props.taxonomy.render({
                            record: item.content.auditRecord,
                            locale: props.locale,
                        });
                        next.push({ recordId, anchoredAt, rendered, fallbackSummary: '' });
                    }
                    catch (e) {
                        next.push({
                            recordId,
                            anchoredAt,
                            rendered: null,
                            fallbackSummary: t('projection.list.render_failed', {
                                detail: e instanceof Error ? e.message : String(e),
                            }),
                        });
                    }
                }
                else {
                    // aggregated-payload — already patient-comprehensible holder
                    // data; surface a brief envelope summary.
                    next.push({
                        recordId,
                        anchoredAt,
                        rendered: null,
                        fallbackSummary: t('projection.list.aggregated_payload_summary', {
                            operationId: item.content.sourceOperationId,
                            receivedAt: item.content.aggregatedAtSubstrate,
                        }),
                    });
                }
            }
            if (!cancelled)
                setItems(next);
        })();
        return () => {
            cancelled = true;
        };
    }, [props.records, props.taxonomy, props.locale, t]);
    return (_jsxs("section", { "aria-labelledby": "projection-list-heading", children: [_jsxs("header", { children: [_jsx("h3", { id: "projection-list-heading", children: t('projection.list.heading') }), props.onRefresh !== undefined ? (_jsx("button", { type: "button", onClick: props.onRefresh, children: t('projection.list.refresh_button') })) : null] }), _jsx("ul", { "aria-label": t('projection.list.aria_label'), children: items.map((item) => (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => props.onSelect(item.recordId), "aria-label": t('projection.list.detail_aria_label', {
                            anchoredAt: item.anchoredAt,
                        }), children: [_jsx("p", { children: item.rendered !== null
                                    ? item.rendered.narrative
                                    : item.fallbackSummary }), _jsx("small", { children: item.anchoredAt })] }) }, item.recordId))) })] }));
}
//# sourceMappingURL=list-step.js.map